import { type PlaySoundMode, loadSound, playSound } from '@suntex.core/audio';

import {
  AUDITORY_MEMORY_SOUND_FILES_SET_ONE,
  AUDITORY_MEMORY_SOUND_FILES_SET_TWO,
  VIF_BOARD_SOUND_FILES,
} from '../../games/data/vifSounds';

export { stopAllSounds, whenSoundQueueIsIdle } from '@suntex.core/audio';

/**
 * The core audio registry is global and other modules register ids like
 * `correct`/`win` against their own files, so VIF's ids are namespaced to
 * avoid colliding with theirs.
 */
export function vifSoundId(id: string): string {
  return `vif-${id}`;
}

/** Loads the legacy board chimes: correct/incorrect/timeout/win. */
export function loadVifBoardSounds(directory: string): void {
  for (const [id, file] of Object.entries(VIF_BOARD_SOUND_FILES)) {
    loadSound(directory + file, vifSoundId(id));
  }
}

/**
 * Loads both Auditory Memory sound sets. Simplest option: the files are
 * small and `loadSound` caches by id+src, so loading both sets regardless of
 * which one a board actually uses costs nothing repeat boards don't already
 * pay for.
 */
export function loadAuditoryMemorySounds(directory: string): void {
  for (const [id, file] of Object.entries({
    ...AUDITORY_MEMORY_SOUND_FILES_SET_ONE,
    ...AUDITORY_MEMORY_SOUND_FILES_SET_TWO,
  })) {
    loadSound(directory + file, vifSoundId(id));
  }
}

export function playVifSound(
  id: string,
  opts?: { mode?: PlaySoundMode },
): void {
  playSound(vifSoundId(id), 1, opts);
}

const EQUATION_PATTERN = /^(\d) × (\d)$/;

/**
 * Speaks an Auditory Memory fact with its answer, e.g. `'2 × 3'` plays
 * `fa23` ("2 3 is 6"). The `×` is U+00D7, as in the data tables. Does
 * nothing for any other equation.
 */
export function playFactAnswer(
  equation: string,
  opts?: { mode: PlaySoundMode },
): void {
  const match = EQUATION_PATTERN.exec(equation);
  if (!match) {
    return;
  }
  const [, a, b] = match;
  playVifSound(`fa${a}${b}`, opts);
}
